import { ChevronDown, ChevronRight, ExternalLink, UtensilsCrossed } from 'lucide-react';
import { usePersistedBoolean } from '../../hooks/usePersistedBoolean';
import { formatDollars } from '../../lib/snapshotMetrics';
import type { MunchFundCampaign, MunchFundCampaignStatus } from '../../types';

const SECTION_KEY = 'crm:orgPage:munchFundSection:expanded';

const STATUS_LABEL: Record<MunchFundCampaignStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  ended: 'Ended',
  archived: 'Archived',
};

const STATUS_COLOR: Record<MunchFundCampaignStatus, string> = {
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  ended: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  archived: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC',
  });
}

function formatDateRange(c: MunchFundCampaign): string | null {
  if (c.startAt && c.endAt) return `${formatDate(c.startAt)} – ${formatDate(c.endAt)}`;
  if (c.startAt) return `Starts ${formatDate(c.startAt)}`;
  if (c.endAt) return `Ends ${formatDate(c.endAt)}`;
  return null;
}

function formatSharePct(bps: number): string {
  return `${Number((bps / 100).toFixed(2))}%`;
}

function isKnownStatus(status: string | null): status is MunchFundCampaignStatus {
  return status !== null && Object.prototype.hasOwnProperty.call(STATUS_LABEL, status);
}

function isHttpUrl(url: string | null): url is string {
  return url !== null && /^https?:\/\//i.test(url);
}

const MunchFundStatusBadge = ({ status }: { status: MunchFundCampaignStatus }) => (
  <span
    data-testid="munchfund-status"
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[status]}`}
  >
    {STATUS_LABEL[status]}
  </span>
);

const HouseTag = () => (
  <span
    data-testid="munchfund-house-tag"
    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
  >
    House
  </span>
);

function totalRaisedCents(campaigns: MunchFundCampaign[]): bigint {
  return campaigns.reduce((sum, c) => sum + (c.raisedCents ? BigInt(c.raisedCents) : 0n), 0n);
}

interface MunchFundFundraisersProps {
  campaigns: MunchFundCampaign[];
}

export const MunchFundFundraisers = ({ campaigns }: MunchFundFundraisersProps) => {
  const [isExpanded, setIsExpanded] = usePersistedBoolean(SECTION_KEY, true);

  if (campaigns.length === 0) return null;

  return (
    <div className="card overflow-hidden" data-testid="munchfund-panel">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
          )}
          <UtensilsCrossed className="h-5 w-5 text-blast-600 dark:text-blast-400 flex-shrink-0" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
            MunchFund Fundraisers
          </h2>
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
          {campaigns.length} {campaigns.length === 1 ? 'campaign' : 'campaigns'}
        </span>
      </button>
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
          {campaigns.map((c) => (
            <CampaignRow key={c.id} campaign={c} />
          ))}
        </div>
      )}
    </div>
  );
};

function CampaignRow({ campaign: c }: { campaign: MunchFundCampaign }) {
  const dates = formatDateRange(c);
  const repName = c.salesRep?.displayName ?? c.repName;
  return (
    <div data-testid="munchfund-row" className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3 px-5 py-3 text-sm">
      <div className="flex flex-col min-w-0 gap-1">
        <div className="flex flex-wrap items-center gap-2">
          {c.title ? (
            <span className="font-medium text-gray-900 dark:text-gray-100">{c.title}</span>
          ) : (
            <span className="font-medium italic text-gray-500 dark:text-gray-400">Untitled campaign</span>
          )}
          {isKnownStatus(c.status) && <MunchFundStatusBadge status={c.status} />}
          {c.kind === 'house' && <HouseTag />}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
          {c.teamName && <span>{c.teamName}</span>}
          {repName && <span>{repName}</span>}
          {dates && <span data-testid="munchfund-dates">{dates}</span>}
          {isHttpUrl(c.storeUrl) && (
            <a
              href={c.storeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blast-600 dark:text-blast-400 hover:text-blast-700 dark:hover:text-blast-300 hover:underline"
            >
              Store <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-gray-600 dark:text-gray-400 sm:flex-shrink-0 sm:gap-x-4">
        {c.goalCents && <span data-testid="munchfund-goal">Goal {formatDollars(c.goalCents)}</span>}
        <span data-testid="munchfund-raised" className="font-medium text-gray-900 dark:text-gray-100">
          {c.raisedCents ? formatDollars(c.raisedCents) : '—'} raised
        </span>
        {c.teamShareBps !== null && (
          <span data-testid="munchfund-share">
            Team {formatSharePct(c.teamShareBps)}
            {c.teamAmountCents ? ` · ${formatDollars(c.teamAmountCents)}` : ''}
          </span>
        )}
        {c.participants !== null && (
          <span data-testid="munchfund-participants">
            {c.withSales !== null ? `${c.withSales} / ${c.participants}` : c.participants} participants
          </span>
        )}
      </div>
    </div>
  );
}

interface MunchFundTeamSliceProps {
  campaigns: MunchFundCampaign[];
}

/** Compact one-line summary for a single team's header. */
export const MunchFundTeamSlice = ({ campaigns }: MunchFundTeamSliceProps) => {
  if (campaigns.length === 0) return null;
  const raised = formatDollars(totalRaisedCents(campaigns).toString());
  return (
    <span
      data-testid="munchfund-team-slice"
      className="text-xs text-[#f05a28]"
      title="MunchFund campaigns for this team"
    >
      MunchFund: {campaigns.length} · {raised}
    </span>
  );
};
